// The ranked queue on this tab (design 14 §6.1–§6.3): what the server says of it, read every two seconds while queued,
// and what the player's asked for. A table forming for a player in a bot game gives them ten seconds; when the game
// starts the tab's game connection takes it there (App's onGame), and if the tab is hidden a notification says so.

import { createEffect, createMemo, createSignal, onCleanup } from "solid-js";
import * as api from "../api";
import type { Length, QueueState } from "../api";
import { t } from "../strings";
import type { PlaySettings } from "./auto";
import { play } from "./sound";

const POLL_MS = 2_000;
const LENGTH_KEY = "mahhua.ranked.length";
const ASKED_KEY = "mahhua.notify.asked";

function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function keep(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Not kept: asked again next time.
  }
}

export interface Queue {
  state: () => QueueState | null;
  /** How long this tab has been queued, in ms, read every second while queued. */
  waited: () => number;
  /** Until the forming table starts, in ms. */
  startsIn: () => number;
  length: () => Length;
  setLength: (l: Length) => void;
  error: () => string;
  join: (l: Length) => Promise<void>;
  leave: () => void;
  joinNow: () => void;
  refresh: () => void;
  /** The account's game just reached this tab: a table from the queue, if it was queued. */
  gameStarted: () => void;
  /** Whether to ask once to be told when a table forms while this tab is hidden. */
  askNotify: () => boolean;
  answerNotify: (yes: boolean) => void;
}

export function createQueue(enabled: () => boolean, settings: () => PlaySettings, go: (p: string) => void): Queue {
  const [state, setState] = createSignal<QueueState | null>(null);
  const [error, setError] = createSignal("");
  const [length, setLen] = createSignal<Length>(stored(LENGTH_KEY) === "east" ? "east" : "east-south");
  const [read, setRead] = createSignal(0); // performance.now() of the last answer
  const [now, setNow] = createSignal(0);
  const [asked, setAsked] = createSignal(stored(ASKED_KEY) !== null);

  const take = (s: QueueState) => {
    setState(s);
    setRead(performance.now());
    if (s.table) arrived(s.table);
  };
  const refresh = () => {
    if (enabled()) api.queueState().then(take, () => {});
  };
  const queued = createMemo(() => !!state()?.queued);

  const told = new Set<string>();
  const arrived = (table: string) => {
    if (told.has(table)) return;
    told.add(table);
    announce();
    go(`/t/${table}`);
  };
  // The sound and, in a hidden tab, the notification (§6.3).
  const announce = () => {
    const s = settings();
    if (s.table_ready) play("ready", s.volume);
    if (document.hidden) notify();
  };

  createEffect(() => {
    if (enabled()) refresh();
    else setState(null);
  });
  createEffect(() => {
    if (!queued()) return;
    const poll = setInterval(refresh, POLL_MS);
    const clock = setInterval(() => setNow(performance.now()), 250);
    setNow(performance.now());
    onCleanup(() => {
      clearInterval(poll);
      clearInterval(clock);
    });
  });

  return {
    state,
    waited: () => (state()?.waited_ms ?? 0) + Math.max(0, now() - read()),
    startsIn: () => Math.max(0, (state()?.starts_ms ?? 0) - Math.max(0, now() - read())),
    length,
    setLength: (l) => {
      setLen(l);
      keep(LENGTH_KEY, l);
    },
    error,
    join: (l) => {
      setError("");
      return api.joinQueue(l).then(take, (err: unknown) => {
        if (err instanceof api.ApiError && typeof err.body.token === "string") go(`/t/${err.body.token}`);
        else setError(err instanceof Error ? err.message : String(err));
      });
    },
    leave: () => void api.leaveQueue().then(take, () => {}),
    joinNow: () => void api.joinQueueNow().then(take, () => {}),
    refresh,
    gameStarted: () => {
      if (queued()) announce();
      refresh();
    },
    askNotify: () => queued() && !asked() && typeof Notification !== "undefined" && Notification.permission === "default",
    answerNotify: (yes) => {
      setAsked(true);
      keep(ASKED_KEY, yes ? "yes" : "no");
      if (yes) void Notification.requestPermission();
    },
  };
}

/** "Your ranked table is ready", shown by the service worker where there is one; clicking it brings the tab forward. */
function notify() {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  const options = { body: t.tableReadyBody, icon: "/icon-192.png", tag: "table-ready" };
  const plain = () => {
    const n = new Notification(t.tableReady, options);
    n.onclick = () => {
      focus();
      n.close();
    };
  };
  const sw = navigator.serviceWorker;
  if (!sw?.controller) return plain();
  sw.ready.then((r) => r.showNotification(t.tableReady, options)).catch(plain);
}
